import { FC } from "react";
import { Link } from "react-router-dom";
import withPage from "../../components/Page";
import Section from "../../components/Section";
import Hero from "../../components/Hero";
import CtaSection from "../../components/CtaSection";
import { globalContent } from "../../config/content";
import styles from "./DigitalBuildingInformation.module.scss";

const DigitalBuildingInformation: FC = () => {
  return (
    <div className={styles.digitalBuildingPage}>
      {/* 1. Hero */}
      <Hero
        eyebrow="Digital Building Information"
        heading="A digital home for your building information."
        copy="Pulse brings your building's documents, drawings, asset information and technical records together in one secure, structured platform — making the information easier to find, understand and use."
        backgroundImage={require("../../assets/images/digital-building-information/digital-building-information-hero.webp")}
        button={{
          text: "Explore the Demo",
          to: globalContent.demoUrl,
          variant: "secondary",
        }}
        secondaryButton={{
          text: "Request a Demo",
          to: "/contact",
          variant: "outlineLight",
        }}
      />

      {/* 2. The Problem */}
      <Section bgColor="off-white">
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>The Challenge</span>
            <h2 className={styles.sectionHeading}>
              Your building has plenty of information. Finding it is the
              problem.
            </h2>
            <p className={styles.sectionCopy}>
              Commercial buildings generate enormous amounts of technical
              information throughout design, construction, fit-out and
              operation. That information often ends up spread across PDFs, CAD
              drawings, O&M manuals, spreadsheets, certificates and project
              folders.
            </p>
            <p className={styles.sectionCopy}>
              When information is fragmented, finding the right document takes
              time and depends on people knowing exactly where to look.
            </p>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/digital-building-information/building-information-fragmented.webp")}
              alt="Fragmented building information sources"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* 3. What Is Digital Building Information? */}
      <Section bgColor="white">
        <div className={styles.splitLayoutReverse}>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/digital-building-information/digital-building-information-structure.webp")}
              alt="Digital building information structure in Pulse"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Platform Overview</span>
            <h2 className={styles.sectionHeading}>
              More than a folder full of documents.
            </h2>
            <p className={styles.sectionCopy}>
              Digital building information is the structured information that
              helps people understand, operate and manage a building. Pulse
              provides a single digital environment for bringing that
              information together.
            </p>
            <p className={styles.sectionCopy}>
              Instead of treating every document as an isolated file, Pulse
              connects information across the building — helping users move
              between documents, drawings, assets and related technical records.
            </p>
          </div>
        </div>
      </Section>

      {/* 4. What Pulse Brings Together */}
      <Section bgColor="off-white">
        <div className={styles.sectionHeader}>
          <span className={styles.eyebrow}>Unified Records</span>
          <h2 className={styles.sectionHeading}>
            One place for the information behind your building.
          </h2>
        </div>
        <div className={styles.cardsGrid}>
          <div className={styles.cardItem}>
            <h3>Documents</h3>
            <p>Technical manuals, specifications, certificates and records.</p>
          </div>
          <div className={styles.cardItem}>
            <h3>Drawings</h3>
            <p>As-built, base-build, CAD files and drawing information.</p>
          </div>
          <div className={styles.cardItem}>
            <h3>Assets</h3>
            <p>Equipment records connected to their documentation.</p>
          </div>
          <div className={styles.cardItem}>
            <h3>O&M Information</h3>
            <p>Handover data used throughout operational life.</p>
          </div>
          <div className={styles.cardItem}>
            <h3>Certifications</h3>
            <p>Compliance records and certificates.</p>
          </div>
          <div className={styles.cardItem}>
            <h3>Maintenance</h3>
            <p>Information for managing equipment and systems.</p>
          </div>
          <div className={styles.cardItem}>
            <h3>Manufacturer Info</h3>
            <p>Datasheets, manuals and supplier details.</p>
          </div>
          <div className={styles.cardItem}>
            <h3>Building Records</h3>
            <p>Design criteria, schedules and technical records.</p>
          </div>
        </div>
      </Section>

      {/* 5. Structure Matters */}
      <Section bgColor="white">
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Information Architecture</span>
            <h2 className={styles.sectionHeading}>
              Information is more useful when it's structured.
            </h2>
            <p className={styles.sectionCopy}>
              Pulse organises building information consistently so users can
              navigate from the building down to systems, assets, documents and
              drawings without friction.
            </p>
            <div className={styles.actionLink}>
              <Link to="/asset-information" className={styles.textLink}>
                Explore Asset Information →
              </Link>
            </div>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/digital-building-information/digital-building-information-connections.webp")}
              alt="Structured building data hierarchy"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* 6. Example: An Asset */}
      <Section bgColor="off-white">
        <div className={styles.splitLayoutReverse}>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/building/building-information.webp")}
              alt="Asset example record in Pulse"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Asset Integration</span>
            <h2 className={styles.sectionHeading}>
              From an asset reference to the information behind it.
            </h2>
            <p className={styles.sectionCopy}>
              For equipment like <strong>AHU-003</strong>, Pulse instantly links
              the asset record to its O&M manual, technical datasheet,
              mechanical drawings, maintenance instructions and warranties.
            </p>
            <div className={styles.actionLink}>
              <Link to="/asset-information" className={styles.textLink}>
                Explore Asset Information →
              </Link>
            </div>
          </div>
        </div>
      </Section>

      {/* 7. Accessible Where Information Is Needed */}
      <Section bgColor="white">
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>On-Site Access</span>
            <h2 className={styles.sectionHeading}>
              From the office to the plant room.
            </h2>
            <p className={styles.sectionCopy}>
              Building information isn't only needed at a desk. Pulse makes
              technical records accessible from mobile devices and tablets,
              helping teams find what they need while working on site.
            </p>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/mobile/engineer-using-pulse.webp")}
              alt="Engineer using Pulse on mobile in plant room"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* 8. Search */}
      <Section bgColor="off-white">
        <div className={styles.splitLayoutReverse}>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/search/search-problem.webp")}
              alt="Pulse search interface"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Powerful Search</span>
            <h2 className={styles.sectionHeading}>
              Find the information behind the building.
            </h2>
            <p className={styles.sectionCopy}>
              Pulse search helps users locate relevant documents, drawings,
              assets and records instantly across the entire building dataset.
            </p>
            <div className={styles.actionLink}>
              <Link to="/search" className={styles.textLink}>
                Explore Search →
              </Link>
            </div>
          </div>
        </div>
      </Section>

      {/* 9. AI */}
      <Section bgColor="white">
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Pulse AI</span>
            <h2 className={styles.sectionHeading}>
              The next step: asking questions about the building.
            </h2>
            <p className={styles.sectionCopy}>
              Once building information is structured, Pulse AI allows users to
              ask natural-language questions and receive source-grounded answers
              backed by the building's own records.
            </p>
            <div className={styles.actionLink}>
              <Link to="/ai" className={styles.textLink}>
                Explore AI →
              </Link>
            </div>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/ai/ai-information.webp")}
              alt="Pulse AI interface"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* 10. Why This Matters / Benefits */}
      <Section bgColor="off-white">
        <div className={styles.sectionHeader}>
          <span className={styles.eyebrow}>Benefits</span>
          <h2 className={styles.sectionHeading}>
            Better access means less time spent looking.
          </h2>
        </div>
        <div className={styles.cardsGrid}>
          <div className={styles.cardItem}>
            <h3>Find information faster</h3>
            <p>
              Reduce time spent searching through disconnected folders and PDFs.
            </p>
          </div>
          <div className={styles.cardItem}>
            <h3>Improve accessibility</h3>
            <p>
              Give teams access to technical data from the office or on site.
            </p>
          </div>
          <div className={styles.cardItem}>
            <h3>Preserve knowledge</h3>
            <p>
              Create a consistent digital record independent of individuals.
            </p>
          </div>
          <div className={styles.cardItem}>
            <h3>Make data useful</h3>
            <p>Bring existing information together without recreating it.</p>
          </div>
        </div>
      </Section>

      {/* 11. Lifecycle */}
      <Section bgColor="white">
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Building Lifecycle</span>
            <h2 className={styles.sectionHeading}>
              Building information shouldn't stop at handover.
            </h2>
            <p className={styles.sectionCopy}>
              Information created during design and construction remains
              valuable throughout operation. Pulse keeps that data accessible
              well beyond project handover.
            </p>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/digital-building-information/digital-building-information-lifecycle.webp")}
              alt="Building lifecycle information flow"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* 12. O&M Relationship */}
      <Section bgColor="off-white">
        <div className={styles.splitLayoutReverse}>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/digital-building-information/digital-building-information-hero.webp")}
              alt="Digital O&M relationship"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Digital O&M</span>
            <h2 className={styles.sectionHeading}>
              A digital building record built from your data.
            </h2>
            <p className={styles.sectionCopy}>
              Pulse provides the digital platform for organising, accessing and
              using O&M documentation and technical records.
            </p>
            <div className={styles.actionLink}>
              <Link to="/digital-om" className={styles.textLink}>
                Explore Digital O&M →
              </Link>
            </div>
          </div>
        </div>
      </Section>

      {/* 13. Final CTA */}
      <CtaSection
        eyebrow="Get Started"
        heading="See what Pulse could do with your building information."
        copy="Whether modernising O&M, improving technical access or creating a digital platform for a building, Pulse provides the structured foundation you need."
        button={{
          text: "Explore the Demo",
          to: globalContent.demoUrl,
          variant: "secondary",
        }}
        secondaryButton={{
          text: "Request a Demo",
          to: "/contact",
          variant: "outlineLight",
        }}
      />
    </div>
  );
};

export default withPage(DigitalBuildingInformation)({
  meta: globalContent.seo.digitalBuildingInformation,
});
